import React, { useRef, useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import Alert from "react-bootstrap/Alert";
import "./FormOne.css";

const budgetScoreMap = {
  "₹15k – ₹25k": 1,
  "₹25k – ₹40k": 2,
  "₹40k – ₹80k": 3,
  "₹1L – ₹3L": 4,
  "₹3L+": 5,
  "₹4L+": 5,
  "₹5L+": 5,
  "₹6L+": 5,
};

const urgencyScoreMap = {
  "Urgent – Start immediately": 5,
  "Decision in a week": 4,
  "Decision in a month": 3,
  "Not in a hurry": 1,
};

const calculateLeadScore = (data) => {
  const budgetScore = budgetScoreMap[data.budget] || 0;
  const urgencyScore = urgencyScoreMap[data.timeline] || 0;
  return budgetScore + urgencyScore;
};

const LeadWizardForm = () => {
  const formRef = useRef();

  const [wizardConfig, setWizardConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [direction, setDirection] = useState(1);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isPreview =
    new URLSearchParams(window.location.search).get("preview") === "1";

  const [leadData, setLeadData] = useState({
    role: "",
    service: "",
    budget: "",
    timeline: "",
    requirement: "",
    name: "",
    email: "",
    phone: "",
    company: "",
    companyPhone: "",
    address: "",
    leadScore: 0,
  });

  const INITIAL_LEAD_STATE = {
    role: "",
    service: "",
    budget: "",
    timeline: "",
    requirement: "",
    name: "",
    email: "",
    phone: "",
    company: "",
    companyPhone: "",
    address: "",
    leadScore: 0,
  };

  useEffect(() => {
    const fetchWizardConfig = async () => {
      try {
        const res = await fetch(
          "https://newwayit.com/adminbackend/getleadformdata.php",
        );
        const data = await res.json();
        setWizardConfig(data);
      } catch (err) {
        console.error("Failed to load wizard config", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWizardConfig();
  }, []);

    useEffect(() => {
    if (isPreview) {
      setSuccess(false);
    }
  }, [isPreview]);

  if (loading) return <p>Loading form…</p>;
  if (!wizardConfig) return <p>Form unavailable</p>;

  const steps = wizardConfig.steps;
  const currentStep = steps[step - 1];
  const progressPercent = (step / steps.length) * 100;

  const handleChange = (e) => {
    setLeadData({ ...leadData, [e.target.name]: e.target.value });
  };

  const prevStep = () => {
    setDirection(-1);
    setStep((s) => s - 1);
  };

  const jumpToStep = (target) => {
    if (target === step) return;
    if (target <= maxStepReached) {
      setDirection(target > step ? 1 : -1);
      setStep(target);
    }
  };

  const selectAndNext = (key, value) => {
    setLeadData((prev) => ({
      ...prev,
      [key]: value,
    }));

    setDirection(1);
    setStep((s) => s + 1);
    setMaxStepReached((m) => Math.max(m, step + 1));
  };

  const nextStep = () => {
    setDirection(1);
    setStep((s) => s + 1);
    setMaxStepReached((m) => Math.max(m, step + 1));
  };

  const resetWizard = () => {
    setLeadData(INITIAL_LEAD_STATE);
    setStep(1);
    setMaxStepReached(1);
    setDirection(1);
  };

  const submitLead = async (e) => {
    e.preventDefault();

    if (isPreview) {
      alert("Preview mode – submission disabled");
      return;
    }

    setSubmitting(true);

    try {
      const leadScore = calculateLeadScore(leadData);
      const payload = { ...leadData, leadScore };

      const res = await fetch(
        "https://newwayit.com/adminbackend/saveleadformdata.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Submission failed");
      }

      setSuccess(true);

      setTimeout(() => {
        setSuccess(false);
        resetWizard();
      }, 1500);
    } catch (err) {
      console.error("Lead submission error:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={submitLead} className="leadwiz-form-root">
      {isPreview && (
        <div className="bg-yellow-100 text-yellow-800 text-sm p-2 rounded mb-3 text-center">
          Preview Mode — submissions are disabled
        </div>
      )}

      <div className="leadwiz-stepper-wrap">
        {steps.map((s, index) => {
          const stepNum = index + 1;
          return (
            <div
              key={s.key}
              className={`leadwiz-step-item
                ${step >= stepNum ? "leadwiz-step-active" : ""}
                ${stepNum <= maxStepReached ? "leadwiz-step-clickable" : ""}`}
              onClick={() => !isPreview && jumpToStep(stepNum)}
            >
              <div className="leadwiz-step-circle">{stepNum}</div>
              <span>{s.title}</span>
            </div>
          );
        })}
      </div>

      <div className="leadwiz-progress-track">
        <div
          className="leadwiz-progress-fill"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: direction * 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -40 }}
          transition={{ duration: 0.25 }}
          className="leadwiz-step-panel"
        >
          {currentStep.type === "options" && (
            <>
              <h4 className="leadwiz-title">{currentStep.title}</h4>

              {Array.isArray(currentStep.options) &&
                currentStep.options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className="btn leadwiz-option-btn"
                    onClick={() => selectAndNext(currentStep.key, opt)}
                  >
                    {opt}
                  </button>
                ))}

              {step > 1 && (
                <button
                  onClick={prevStep}
                  type="button"
                  className="leadwiz-back-btn"
                >
                  ← Back
                </button>
              )}
            </>
          )}

          {currentStep.type === "budget" && (
            <>
              <h4 className="leadwiz-title">{currentStep.title}</h4>

              {currentStep.options[leadData[currentStep.dependsOn]]?.map(
                (b) => (
                  <button
                    key={b}
                    type="button"
                    className="btn leadwiz-option-btn"
                    onClick={() => selectAndNext(currentStep.key, b)}
                  >
                    {b}
                  </button>
                ),
              )}

              <button
                onClick={prevStep}
                type="button"
                className="leadwiz-back-btn"
              >
                ← Back
              </button>
            </>
          )}

          {currentStep.type === "textarea" && (
            <>
              <h4 className="leadwiz-title">{currentStep.title}</h4>
              <textarea
                name={currentStep.key}
                rows="4"
                className="form-control leadwiz-textarea"
                onChange={handleChange}
                required
              />
              <div className="leadwiz-nav-row">
                <button
                  onClick={prevStep}
                  type="button"
                  className="leadwiz-back-btn"
                >
                  ← Back
                </button>
                <button
                  onClick={nextStep}
                  type="button"
                  className="leadwiz-next-btn"
                  disabled={!leadData[currentStep.key]}
                >
                  Next →
                </button>
              </div>
            </>
          )}

          {currentStep.type === "contact" && (
            <>
              <h4 className="leadwiz-title">{currentStep.title}</h4>

              <input
                name="name"
                placeholder="Name"
                className="form-control leadwiz-input"
                onChange={handleChange}
                required
              />
              <input
                name="email"
                type="email"
                placeholder="Email"
                className="form-control leadwiz-input"
                onChange={handleChange}
                required
              />
              <input
                name="phone"
                placeholder="Phone"
                className="form-control leadwiz-input"
                onChange={handleChange}
                required
              />
              <input
                name="company"
                placeholder="Company Name"
                className="form-control leadwiz-input"
                onChange={handleChange}
              />
              <input
                name="companyPhone"
                placeholder="Company Number"
                className="form-control leadwiz-input"
                onChange={handleChange}
              />
              <textarea
                name="address"
                placeholder="Address"
                className="form-control leadwiz-textarea"
                onChange={handleChange}
              />

              <div className="leadwiz-nav-row">
                <button
                  onClick={prevStep}
                  type="button"
                  className="leadwiz-back-btn"
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="leadwiz-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {success && (
        <Alert variant="success" className="leadwiz-success-msg">
          Lead submitted successfully
        </Alert>
      )}
    </form>
  );
};

export default LeadWizardForm;
